// Search-filter builders for ExtendedSearchPage. Ported from workshop's
// workshop/api/queries/search.js - pure functions, no GraphQL imports.
// The where-clause objects target genesis's PersonFilterInput /
// SharedItemFilterInput shapes; we type the output loosely and let the
// query layer cast to the generated input types at the call site (same
// pattern BasicsPage uses for UpdatePerson).

const CI = "CASE_INSENSITIVE";

const AGE_MIN = 0;
const AGE_MAX = 120;
const CHILDREN_MIN = 0;
const CHILDREN_MAX = 20;

export interface ExtendedPersonFilters {
  q: string;
  firstName: string;
  lastName: string;
  ageMin: number;
  ageMax: number;
  genderIds: readonly string[];
  maritalStatusIds: readonly string[];
  childCountMin: number;
  childCountMax: number;
  city: string;
  countryIds: readonly string[];
  citizenshipIds: readonly string[];
  nationality: string;
  languageCodes: readonly string[];
  expertise: string;
  jobTitle: string;
  department: string;
  industryIds: readonly string[];
  location: string;
}

export const EMPTY_EXTENDED_FILTERS: ExtendedPersonFilters = {
  q: "",
  firstName: "",
  lastName: "",
  ageMin: AGE_MIN,
  ageMax: AGE_MAX,
  genderIds: [],
  maritalStatusIds: [],
  childCountMin: CHILDREN_MIN,
  childCountMax: CHILDREN_MAX,
  city: "",
  countryIds: [],
  citizenshipIds: [],
  nationality: "",
  languageCodes: [],
  expertise: "",
  jobTitle: "",
  department: "",
  industryIds: [],
  location: "",
};

export const PERSON_AGE_MIN = AGE_MIN;
export const PERSON_AGE_MAX = AGE_MAX;
export const PERSON_CHILDREN_MIN = CHILDREN_MIN;
export const PERSON_CHILDREN_MAX = CHILDREN_MAX;

const contains = (value: string) => ({
  contains: value.trim(),
  caseSensitivity: CI,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Where = Record<string, any>;

// Builds the PersonFilterInput from the filter state. Returns null when no
// criterion is set (caller skips the query).
export function buildExtendedWhere(f: ExtendedPersonFilters): Where | null {
  const and: Where[] = [];

  // NOTE: the free-text `q` is intentionally NOT folded into the filter. It is
  // handed to genesis' globalSearch as the full-text query (cross-fragment,
  // multi-term), while this builder only produces the structured PersonFilter
  // that globalSearch applies as `personFilter`. Putting `q` into
  // formattedName here would AND a single-field contains over the whole string
  // and defeat the cross-field search.
  if (f.firstName.trim()) and.push({ name: { given: { some: contains(f.firstName) } } });
  if (f.lastName.trim()) and.push({ name: { family: contains(f.lastName) } });

  if (f.ageMin > AGE_MIN) and.push({ ageInYears: { ge: f.ageMin } });
  if (f.ageMax < AGE_MAX) and.push({ ageInYears: { le: f.ageMax } });

  if (f.genderIds.length) and.push({ gender: { id: { in: f.genderIds } } });
  if (f.maritalStatusIds.length) and.push({ maritalStatus: { id: { in: f.maritalStatusIds } } });

  if (f.childCountMin > CHILDREN_MIN) and.push({ childCount: { ge: f.childCountMin } });
  if (f.childCountMax < CHILDREN_MAX) and.push({ childCount: { le: f.childCountMax } });

  if (f.city.trim()) and.push({ city: contains(f.city) });
  if (f.countryIds.length) and.push({ country: { id: { in: f.countryIds } } });
  if (f.citizenshipIds.length)
    and.push({ citizenships: { some: { id: { in: f.citizenshipIds } } } });
  if (f.nationality.trim()) and.push({ nationality: contains(f.nationality) });

  if (f.languageCodes.length)
    and.push({ languageSkills: { some: { languageTag: { in: f.languageCodes } } } });
  if (f.expertise.trim())
    and.push({ expertises: { some: { description: contains(f.expertise) } } });
  if (f.jobTitle.trim()) and.push({ jobTitle: contains(f.jobTitle) });
  if (f.department.trim()) and.push({ jobDepartment: { name: contains(f.department) } });
  if (f.industryIds.length)
    and.push({ experiences: { some: { industry: { id: { in: f.industryIds } } } } });
  if (f.location.trim()) and.push({ jobLocation: { name: contains(f.location) } });

  if (and.length === 0) return null;
  if (and.length === 1) return and[0]!;
  return { and };
}

// === Company filters ===

// A headcount range needs two bounds even though the upper one is open-ended in reality. The cap is
// set well above any real company so that leaving it alone means "no upper bound": isFieldActive
// treats max === COMPANY_SIZE_MAX as untouched and buildCompanyWhere emits no `le` clause for it, so
// a company of 300,000 is still returned by a filter left at the top of its range.
const SIZE_MIN = 0;
const SIZE_MAX = 250000;

export const COMPANY_SIZE_MIN = SIZE_MIN;
export const COMPANY_SIZE_MAX = SIZE_MAX;

/**
 * The company tab's structured criteria.
 *
 * The searchable TEXT is fixed by company-profiles/0005-company-in-search §2 (name, expertise,
 * industry) and lives in genesis's trigger. These are the STRUCTURED facets applied on top, and they
 * deliberately stay inside that same set: there is no `summary` criterion here, because the prose
 * that §2 kept out of the index must not become reachable through a filter instead.
 */
export interface CompanyFilters {
  q: string;
  industryIds: readonly string[];
  // Free text matched against the company's own expertise LABELS, several terms at a time. Since
  // concept v4 those labels are the company's free text rather than entries in a shared catalogue. A
  // picker over the curated catalogue was tried first and was wrong twice over: it rendered a
  // different control from the one beside it, and typing into it filtered a list instead of setting
  // the filter, so the pill never showed a value.
  expertises: readonly string[];
  employeeCountMin: number;
  employeeCountMax: number;
}

export const EMPTY_COMPANY_FILTERS: CompanyFilters = {
  q: "",
  industryIds: [],
  expertises: [],
  employeeCountMin: SIZE_MIN,
  employeeCountMax: SIZE_MAX,
};

/**
 * Builds the CompanyFilterInput from the filter state, or null when nothing is set.
 *
 * Same shape as buildExtendedWhere, and for the same reason: genesis's CompanyFilterSpec matches ONE
 * criterion per filter object and returns, so two criteria passed side by side in one object would
 * silently apply only the first. Multiple criteria must therefore travel as `and`.
 */
export function buildCompanyWhere(f: CompanyFilters): Where | null {
  const and: Where[] = [];

  // `q` stays out, exactly as it does for persons: it is globalSearch's full-text query across the
  // company's whole indexed vector. Folding it in here would narrow it to a single-field contains.
  //
  // THERE IS NO NAME FACET, deliberately. The search box above already matches the company name at
  // the highest weight in the vector (0005 §2), so a name pill would be a second, worse way to do
  // what the box does — narrower, because it matches one field instead of the whole vector.
  if (f.industryIds.length) and.push({ industry: { id: { in: f.industryIds } } });
  // SEVERAL terms mean "any of these capabilities", not all: a company is looked for by the things
  // it can do, and requiring every term would return nothing for a perfectly good two-term search.
  // The `or` rides the single competency join genesis already makes, so a company matching two terms
  // still comes back once (the resolver groups by entity).
  const expertises = f.expertises.map((e) => e.trim()).filter(Boolean);
  if (expertises.length === 1) and.push({ competencyFieldName: contains(expertises[0]!) });
  else if (expertises.length > 1)
    and.push({ competencyFieldName: { or: expertises.map((e) => contains(e)) } });
  if (f.employeeCountMin > SIZE_MIN) and.push({ employeeCount: { ge: f.employeeCountMin } });
  if (f.employeeCountMax < SIZE_MAX) and.push({ employeeCount: { le: f.employeeCountMax } });

  if (and.length === 0) return null;
  if (and.length === 1) return and[0]!;
  return { and };
}

// === SharedItem filters ===

export const SHARED_FLAG_KEYS = ["owner", "pinned", "signedUp", "rated", "commented"] as const;
export type SharedFlagKey = (typeof SHARED_FLAG_KEYS)[number];

// "Share" combines both shared-item types; the itemType sub-filter narrows to
// one. "ALL" applies no itemType clause (POST + PROJECT are the only types).
export type SharedItemTypeFilter = "ALL" | "PROJECT" | "POST";

// Who published the item, which since company profiles is a different question from who wrote it:
// `shared_item.company_id` is set for an item published in a company's name and null for a personal
// one, and every item is exactly one of the two.
export type SharedItemPublisherFilter = "ALL" | "COMPANY" | "PERSON";

export interface SharedItemFilters {
  // Single free-text field — matched against name + shortDescription +
  // description (the Suchbegriff covers all three, so no separate Beschreibung).
  q: string;
  itemType: SharedItemTypeFilter;
  publisher: SharedItemPublisherFilter;
  owner: boolean | null;
  pinned: boolean | null;
  signedUp: boolean | null;
  rated: boolean | null;
  commented: boolean | null;
}

export const EMPTY_SHARED_FILTERS: SharedItemFilters = {
  q: "",
  itemType: "ALL",
  publisher: "ALL",
  owner: null,
  pinned: null,
  signedUp: null,
  rated: null,
  commented: null,
};

export function sharedHasCriteria(f: SharedItemFilters): boolean {
  if (f.q.trim() || f.itemType !== "ALL") return true;
  if (f.publisher !== "ALL") return true;
  return SHARED_FLAG_KEYS.some((k) => f[k] === true || f[k] === false);
}

// Returns null when no criterion is set — the caller must then OMIT `where`
// entirely. genesis' sharedItems resolver errors (returns null for the
// NonNull connection) on an empty `where: {}`, so "Alle ohne Filter" must send
// no where at all (like the discover feed did).
export function buildSharedItemWhere(f: SharedItemFilters): Where | null {
  const and: Where[] = [];
  if (f.itemType !== "ALL") and.push({ itemType: { eq: f.itemType } });
  // The publisher question rides `company.name` over genesis's LEFT join: `any: true` is "there is a
  // company on the other side", `any: false` is "there is not". Naming ONE company is deliberately
  // absent — you cannot search a personal item by its author either, so a company box would be the
  // only by-whom criterion on the panel and would read as an oversight on the other half.
  if (f.publisher !== "ALL")
    and.push({ company: { name: { any: f.publisher === "COMPANY" } } });
  if (f.q.trim())
    and.push({
      or: [
        { name: contains(f.q) },
        { shortDescription: contains(f.q) },
        { description: contains(f.q) },
      ],
    });
  for (const key of SHARED_FLAG_KEYS) {
    if (f[key] === true || f[key] === false) and.push({ [key]: { eq: f[key] } });
  }
  if (and.length === 0) return null;
  if (and.length === 1) return and[0]!;
  return { and };
}
